import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginWithEmailPassword } from '../lib/auth';
import { useCurrentUser } from '../contexts/UserContext';
import { sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../lib/firebase';
import GoogleAuthButton from '../components/GoogleAuthButton';

export default function SignIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { firebaseUser, loading: authLoading } = useCurrentUser();

  // Redirect after auth, respecting redirectTo query param
  useEffect(() => {
    if (!authLoading && firebaseUser && !firebaseUser.isAnonymous) {
      const redirectTo = searchParams.get('redirectTo');
      if (redirectTo) {
        const decodedPath = decodeURIComponent(redirectTo);
        // Security: only allow relative paths starting with /
        if (decodedPath.startsWith('/')) {
          navigate(decodedPath, { replace: true });
          return;
        }
      }
      navigate('/dashboard', { replace: true });
    }
  }, [firebaseUser, authLoading, navigate, searchParams]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await loginWithEmailPassword(email, password);
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      // Navigation handled by useEffect watching auth state
    } catch (error: any) {
      let errorMessage = t('errors.generic');
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = t('errors.noAccount');
          break;
        case 'auth/wrong-password':
          errorMessage = t('errors.wrongPassword');
          break;
        case 'auth/too-many-requests':
          errorMessage = t('errors.tooManyRequests');
          break;
        case 'auth/user-disabled':
          errorMessage = t('errors.accountDisabled');
          break;
        default:
          errorMessage = error.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // Navigation handled by useEffect watching auth state
    } catch (error: any) {
      let errorMessage = t('errors.googleGeneric');
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = t('errors.popupClosed');
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = t('errors.popupBlocked');
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = t('errors.unauthorizedDomain');
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (error: any) {
      let errorMessage = t('errors.resetGeneric');
      if (error.code === 'auth/user-not-found') {
        errorMessage = t('errors.noAccount');
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Enable scrolling for this standalone page (not wrapped in BaseLayout)
  useEffect(() => {
    document.documentElement.classList.add('allow-scroll');
    document.body.classList.add('allow-scroll');
    document.getElementById('root')?.classList.add('allow-scroll');
    return () => {
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
      document.getElementById('root')?.classList.remove('allow-scroll');
    };
  }, []);

  // Load remembered email on component mount
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  return (
    <div
      data-testid="page-auth"
      className="min-h-screen flex items-center justify-center"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px), url('/images/satin-phone-bg.png')",
        backgroundRepeat: 'repeat, no-repeat',
        backgroundSize: '30px 30px, cover',
        backgroundPosition: 'center center',
      }}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 w-full max-w-md p-8">
        <div className="bg-black/60 backdrop-blur-md p-8 rounded-lg shadow-xl">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            {showResetPassword ? t('resetPassword.title') : t('signIn.title')}
          </h2>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-white px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {resetSent ? (
            <div className="bg-green-500/20 border border-green-500 text-white px-4 py-3 rounded mb-4">
              {t('resetPassword.success')}
            </div>
          ) : showResetPassword ? (
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div>
                <label htmlFor="reset-email" className="block text-white mb-2">
                  {t('signIn.email')}
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 rounded bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                  placeholder={t('signIn.emailPlaceholder')}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-semibold py-3 rounded-lg hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
              </button>

              <button
                type="button"
                onClick={() => setShowResetPassword(false)}
                className="w-full text-white hover:text-yellow-400 transition"
              >
                {t('resetPassword.backToSignIn')}
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleEmailSignIn} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-white mb-2">
                    {t('signIn.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 rounded bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                    placeholder={t('signIn.emailPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-white mb-2">
                    {t('signIn.password')}
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                    placeholder={t('signIn.passwordPlaceholder')}
                    required
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 text-white/80">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="rounded border-white/20 text-yellow-400 focus:ring-yellow-400"
                    />
                    <span>{t('signIn.rememberMe')}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(true)}
                    className="text-yellow-400 hover:text-yellow-300 text-sm"
                  >
                    {t('signIn.forgotPassword')}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-semibold py-3 rounded-lg hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('signIn.submitting') : t('signIn.title')}
                </button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/20"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-black/60 text-white/60">{t('social.continueWith')}</span>
                  </div>
                </div>

                <GoogleAuthButton
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="mt-6"
                >
                  {t('social.signInGoogle')}
                </GoogleAuthButton>
              </div>

              <p className="mt-8 text-center text-white/60">
                {t('signIn.noAccount')}{' '}
                <Link to="/get-started" className="text-yellow-400 hover:text-yellow-300">
                  {t('signIn.signUp')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 
